import { IonCard, IonIcon } from '@ionic/react'
import dayjs from 'dayjs'
import { arrowDown, arrowUp, chevronForward, flame, flash } from 'ionicons/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { getYearAgoChange, MonthlyReport } from '../utils'

interface Props {
	report: MonthlyReport
	allReports: MonthlyReport[]
	onClick: () => void
}

const ReportListCard: React.FC<Props> = ({ report, allReports, onClick }) => {
	const { t } = useTranslation('pages_monthlyReportsList')

	const monthNumber = dayjs(report.from).format('M')
	const year = dayjs(report.from).format('YYYY')

	const monthsName = dayjs(report.from).format('MMMM')
	const capitalizedMonthsName = monthsName.charAt(0).toUpperCase() + monthsName.slice(1)

	const consumptionChange = getYearAgoChange('totalConsumption', report, allReports)
	const costChange = getYearAgoChange('totalBill', report, allReports)

	return (
		<IonCard
			onClick={onClick}
			className='flex flex-col shadow-card bg-white dark:bg-brand-secondary-200-dark group hover'
		>
			<div className='flex items-center'>
				<div className='flex flex-col text-center'>
					<span className='text-[2.4rem] font-bold -mb-[0.7rem] text-brand-secondary-100-light dark:text-brand-primary-100-dark'>
						{monthNumber}
					</span>
					<span className='font-bold text-brand-primary-100-light dark:text-brand-secondary-100-dark'>
						{year}
					</span>
				</div>
				<div className='flex flex-col justify-center ml-[1.4rem]'>
					<div className='text-[0.8rem] text-text-grey-200 dark:text-text-grey-100'>
						{t('kuukausiraportti')}
					</div>
					<span className='font-bold mt-[0.35rem] tracking-wide text-text-dark dark:text-white'>
						{capitalizedMonthsName}
					</span>
				</div>

				<div className='grow' />

				{(consumptionChange !== null || costChange !== null) && (
					<div className='flex flex-col items-end gap-[0.15rem] mr-[0.8rem]'>
						{consumptionChange !== null && (
							<div
								className={`flex items-center font-bold text-[0.75rem] ${
									consumptionChange <= 0 ? 'text-green-600' : 'text-red-500'
								}`}
							>
								<IonIcon className='text-[0.7rem] mr-[0.1rem]' icon={flash} />
								<IonIcon
									className='text-[0.9rem]'
									icon={consumptionChange <= 0 ? arrowDown : arrowUp}
								/>
								<span>{`${Math.abs(consumptionChange).toFixed(1)}%`}</span>
							</div>
						)}
						{costChange !== null && (
							<div
								className={`flex items-center font-bold text-[0.75rem] ${
									costChange <= 0 ? 'text-green-600' : 'text-red-500'
								}`}
							>
								<span className='text-[0.7rem] mr-[0.15rem]'>{'€'}</span>
								<IonIcon className='text-[0.9rem]' icon={costChange <= 0 ? arrowDown : arrowUp} />
								<span>{`${Math.abs(costChange).toFixed(1)}%`}</span>
							</div>
						)}
						<span className='text-[0.65rem] font-normal text-text-grey-200 dark:text-text-grey-100'>
							{t('vsViimeVuosi')}
						</span>
					</div>
				)}

				<IonIcon
					className='text-[2.15rem] transition-transform group-hover:scale-[1.2] group-hover:transition-transform text-text-grey-200 dark:text-text-grey-100'
					icon={chevronForward}
				/>
			</div>

			<div className='flex gap-6 mt-[0.9rem] pt-[0.9rem] border-t border-[#e5e5e5] dark:border-brand-primary-200-dark'>
				<div className='flex items-center gap-[0.4rem]'>
					<IonIcon
						className='text-[1.2rem] text-brand-primary-100-light dark:text-brand-primary-100-dark'
						icon={flash}
					/>
					<span className='font-bold text-text-dark dark:text-white'>
						{`${report.totalConsumption} kWh`}
					</span>
					<span className='font-bold text-text-grey-200 dark:text-text-grey-100 ml-[0.6rem]'>
						{`${report.totalBill.toFixed(2)} €`}
					</span>
				</div>

				{report.heatingConsumption > 0 && (
					<div className='flex items-center gap-[0.4rem]'>
						<IonIcon
							className='text-[1.2rem] text-brand-primary-100-light dark:text-brand-primary-100-dark'
							icon={flame}
						/>
						<span className='font-bold text-text-dark dark:text-white'>
							{`${report.heatingConsumption} kWh`}
						</span>
					</div>
				)}
			</div>
		</IonCard>
	)
}

export default ReportListCard
